import { MarkdownBody } from '@inflearn/ds-markdown-body';
import {
  Box,
  Button,
  COLOR_PALETTE,
  Group,
  Icon,
  Tabs,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { filterParagraphTag } from '@inflearn/editor';
import { faAngleDown, faAngleUp } from '@inflearn/pro-regular-svg-icons';
import { useDisclosure } from '@mantine/hooks';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import useCheckMyProfile from '../../../../hooks/useCheckMyProfile';
import useInstructorProfileQuery from '../../../../hooks/useInstructorProfileQuery';
import useQueryParams from '../../../../hooks/useQueryParams';
import useUserProfileQuery from '../../../../hooks/useUserProfileQuery';
import { eventNames } from '../../../../utils/GTM/eventNames';
import { useTrackProfileEvent } from '../../../../utils/GTM/useTrackProfileEvent';
import EmptyStates from '../../../common/EmptyStates';
import { IntroSectionBox } from '../IntroSectionBox/IntroSectionBox';
import IntroductionEditorModal from '../IntroduceEditorModal/introduceEditorModal';

const INTRO_TYPE = {
  PERSONAL: 'PERSONAL',
  MENTORING: 'MENTORING',
} as const;

type IntroductionProps = {
  isActiveMentor: boolean;
};

export function Introduction({ isActiveMentor }: IntroductionProps) {
  const { t } = useTranslation();
  const { isMe } = useCheckMyProfile();
  const { id } = useQueryParams();
  const userId = Number(id || 0);
  const router = useRouter();

  const [
    isIntroductionEditorModalOpened,
    { open: openIntroductionEditorModal, close: closeIntroductionEditorModal },
  ] = useDisclosure(false);

  const { data: profile } = useUserProfileQuery({ id: userId });
  const { data: instructorProfile } = useInstructorProfileQuery({
    userId: String(userId),
    isInstructor: isActiveMentor,
  });

  const introduceBody = profile?.introduce || '';
  const mentoringIntroduceBody = instructorProfile?.mentor?.introduction || '';

  const visibleMentoring = isActiveMentor && !isEmpty(filterParagraphTag(mentoringIntroduceBody));
  const isMentoringOnly = isEmpty(filterParagraphTag(introduceBody)) && visibleMentoring;

  const [type, setType] = useState<keyof typeof INTRO_TYPE>(() => {
    if (isMentoringOnly) {
      return INTRO_TYPE.MENTORING;
    }

    return INTRO_TYPE.PERSONAL;
  });

  const isTypeMentoring = type === INTRO_TYPE.MENTORING;
  const { shouldOpenIntroductionEditorModal, removeQueryParams } =
    useOpenIntroductionModalByQueryParams();

  useEffect(() => {
    if (!shouldOpenIntroductionEditorModal) {
      return;
    }

    if (!isMe || !router.isReady) {
      return;
    }

    openIntroductionEditorModal();
    removeQueryParams();
  }, [shouldOpenIntroductionEditorModal, isMe, openIntroductionEditorModal, router]);

  return (
    <Tabs
      color={'dark'}
      value={type}
      onTabChange={(value: keyof typeof INTRO_TYPE) => setType(value)}
      variant={'default'}
      styles={{
        tab: {
          padding: '8px 0',
          fontWeight: 700,
          fontSize: 20,
          color: COLOR_PALETTE.gray[6],
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        tabsList: {
          gap: 16,
          borderBottom: 'none',
        },
      }}
    >
      <IntroSectionBox
        styles={{
          root: {
            borderTop: 'none',
            [desktopFirstMediaQuery.tablet]: { paddingTop: 0 },
            [desktopFirstMediaQuery.mobile]: { paddingTop: 20 },
          },
          header: {
            marginBottom: 20,
          },
        }}
        title={
          <Tabs.List>
            <Tabs.Tab value={INTRO_TYPE.PERSONAL}>{t('소개')}</Tabs.Tab>
            {visibleMentoring && <Tabs.Tab value={INTRO_TYPE.MENTORING}>{t('멘토링')}</Tabs.Tab>}
          </Tabs.List>
        }
        titleRightChildren={
          isMe && !isTypeMentoring ? (
            <Button variant="default" size="sm" radius="md" onClick={openIntroductionEditorModal}>
              {introduceBody ? t('수정하기') : t('작성하기')}
            </Button>
          ) : null
        }
      >
        <Tabs.Panel value={INTRO_TYPE.PERSONAL}>
          <IntroductionEditorModal
            opened={isIntroductionEditorModalOpened}
            onClose={closeIntroductionEditorModal}
            editorInitialValue={introduceBody}
            userId={userId}
          />
          <EmptyStates
            isEmpty={isEmpty(filterParagraphTag(introduceBody))}
            empty={{
              image: '',
              title: t('소개글이 비어있어요'),
              body: isMe ? t('나만의 스킬, 깃허브 링크 등으로 소개글을 채워보세요.') : '',
            }}
          >
            <ExpandableMarkdownContent content={introduceBody} />
          </EmptyStates>
        </Tabs.Panel>
        {visibleMentoring && mentoringIntroduceBody && (
          <Tabs.Panel value={INTRO_TYPE.MENTORING}>
            <ExpandableMarkdownContent
              content={mentoringIntroduceBody}
              defaultExpanded={
                isMentoringOnly && isTypeMentoring && instructorProfile?.courseCount === 0
              }
            />
          </Tabs.Panel>
        )}
      </IntroSectionBox>
    </Tabs>
  );
}

const useOpenIntroductionModalByQueryParams = () => {
  const searchParams = useSearchParams();
  const action = searchParams.get('action');
  const target = searchParams.get('target');
  const router = useRouter();

  const shouldOpenIntroductionEditorModal = action === 'modal' && target === 'introduction';

  const removeQueryParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('action');
    searchParams.delete('target');
    const nextSearch = searchParams.toString();
    const nextUrl = nextSearch
      ? `${window.location.pathname}?${nextSearch}`
      : window.location.pathname;

    void router.replace(nextUrl, undefined, { shallow: true });
  };

  return {
    shouldOpenIntroductionEditorModal,
    removeQueryParams,
  };
};

function ExpandableMarkdownContent({
  content,
  defaultExpanded = false,
}: { content: string; defaultExpanded?: boolean }) {
  const CONTENT_MAX_HEIGHT = 240;
  const contentRef = useRef<HTMLDivElement | null>(null);

  const { trackMixpanelEvent } = useTrackProfileEvent();
  const { t } = useTranslation();

  const [isFirstMount, setIsFirstMount] = useState(true);
  const [isShowMoreButton, setIsShowMoreButton] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const isFoldedContent = isShowMoreButton && !isExpanded;

  useEffect(
    function updateHeight() {
      const contentSection = contentRef.current;

      if (!contentSection) {
        return;
      }

      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setIsShowMoreButton(contentSection.scrollHeight > CONTENT_MAX_HEIGHT);
        }
      });

      observer.observe(contentSection);

      setTimeout(() => {
        setIsFirstMount(false);
      }, 100);

      return () => {
        observer.disconnect();
      };
    },
    [content],
  );

  const handleFoldClick = () => {
    setIsExpanded((prev) => {
      const updateState = !prev;

      // GTM
      trackMixpanelEvent(eventNames.CLICK_MORE_INFO, {
        more_view: updateState,
      });

      return updateState;
    });
  };

  return (
    <>
      <div
        ref={contentRef}
        css={{
          position: 'relative',
          overflow: isFirstMount || isFoldedContent ? 'hidden' : 'visible',
          maxHeight: isFirstMount || isFoldedContent ? CONTENT_MAX_HEIGHT : 'none',
        }}
      >
        <MarkdownBody html={content} />
        {isFoldedContent ? (
          <Box
            css={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              height: 60,
              width: '100%',
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)',
            }}
          />
        ) : null}
      </div>
      {isShowMoreButton && (
        <Group position="center">
          <Button
            mt={20}
            size="sm"
            color="gray"
            variant="subtle"
            rightIcon={
              isExpanded ? (
                <Icon icon={faAngleUp} size="sm" color="gray.6" />
              ) : (
                <Icon icon={faAngleDown} size="sm" color="gray.6" />
              )
            }
            onClick={handleFoldClick}
          >
            {isExpanded ? t('접기') : t('더보기')}
          </Button>
        </Group>
      )}
    </>
  );
}
